import React, {useState, useCallback} from 'react';
import {Autocomplete, Libraries, useJsApiLoader} from '@react-google-maps/api';
import {TextField, Box} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {env} from '@/config';
import {AddressDetails} from '@/types/billing-data';

const {GOOGLE_MAPS_API_KEY} = env;
const LIBRARIES = ['places'] as Libraries;

interface LocationAutocompleteProps {
  onAddressSelected: (address: AddressDetails) => void;
  touched?: boolean;
  error?: {
    street?: string;
    streetNumber?: string;
    postalCode?: string;
    city?: string;
    country?: string;
    fullAddress?: string;
  };
  initialAddress?: string;
}
const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  onAddressSelected,
  touched,
  error,
  initialAddress = ''
}) => {
  const {t} = useTranslation();
  const [address, setAddress] = useState<string>(initialAddress);
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const {isLoaded} = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES
  });

  const onLoad = useCallback(
    (autocompleteInstance: google.maps.places.Autocomplete) => {
      const options = {
        componentRestrictions: {country: 'de'}
      };
      autocompleteInstance.setOptions(options);
      setAutocomplete(autocompleteInstance);
    },
    []
  );

  const onPlaceChanged = useCallback(() => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();

      if (!place.geometry || !place.geometry.location) {
        return;
      }

      const addressComponents = place.address_components;
      const formattedAddress = place.formatted_address || '';

      const addressDetails: AddressDetails = {
        street: '',
        streetNumber: '',
        postalCode: '',
        city: '',
        country: '',
        fullAddress: formattedAddress
      };

      addressComponents?.forEach((component) => {
        const types = component.types;

        if (types.includes('route')) {
          addressDetails.street = component.long_name;
        }
        if (types.includes('street_number')) {
          addressDetails.streetNumber = component.long_name;
        }
        if (types.includes('postal_code')) {
          addressDetails.postalCode = component.long_name;
        }
        if (types.includes('locality')) {
          addressDetails.city = component.long_name;
        }
        if (types.includes('country')) {
          addressDetails.country = component.long_name;
        }
      });

      setAddress(formattedAddress);

      if (onAddressSelected) {
        onAddressSelected(addressDetails);
      }
    }
  }, [autocomplete, onAddressSelected]);

  const isFullAddressEmpty = !address;

  return (
    <Box>
      {isLoaded && (
        <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
          <TextField
            fullWidth
            label={t('address')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            error={touched && error ? true : false}
            helperText={
              touched &&
              (isFullAddressEmpty
                ? t('validation:is_required', {
                    name: t('default:address')
                  })
                : error
                ? error.street ||
                  error.streetNumber ||
                  error.postalCode ||
                  error.city ||
                  error.country
                : undefined)
            }
          />
        </Autocomplete>
      )}
    </Box>
  );
};

export default LocationAutocomplete;
